import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { CreditCard, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from "../../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const AdminAccounts = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/admin/accounts`, { headers, params: { status_filter: filter } });
      setAccounts(res.data.accounts);
      setTotal(res.data.total);
    } catch {}
  };

  useEffect(() => { fetchAccounts(); }, [filter]);

  const updateAccount = async (subId, data) => {
    try {
      await axios.put(`${API}/admin/accounts/${subId}`, data, { headers });
      toast({ title: 'Account updated' });
      fetchAccounts();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  return (
    <div data-testid="admin-accounts-page">
      <h1 className="text-2xl font-bold text-white mb-1">Accounts</h1>
      <p className="text-gray-500 text-sm mb-6">Manage subscriptions ({total} total)</p>

      <div className="flex gap-2 mb-4">
        {['', 'active', 'trial', 'cancelled', 'expired'].map(f => (
          <Button key={f} onClick={() => setFilter(f)} size="sm" className={`text-xs ${filter === f ? 'bg-emerald-600 text-white' : 'bg-[#132218] text-gray-400 border border-emerald-900/30 hover:text-white'}`} data-testid={`filter-${f || 'all'}`}>
            {f || 'All'}
          </Button>
        ))}
      </div>

      <Card className="bg-[#132218] border-emerald-900/30">
        <CardContent className="p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-emerald-900/30">
                <th className="text-left text-xs text-gray-500 font-medium p-3">User</th>
                <th className="text-left text-xs text-gray-500 font-medium p-3">Plan</th>
                <th className="text-left text-xs text-gray-500 font-medium p-3">Status</th>
                <th className="text-left text-xs text-gray-500 font-medium p-3">Amount</th>
                <th className="text-right text-xs text-gray-500 font-medium p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(acc => (
                <tr key={acc.id} className="border-b border-emerald-900/10 hover:bg-white/[0.02]" data-testid={`account-row-${acc.id}`}>
                  <td className="p-3">
                    <p className="text-white text-sm">{acc.user?.full_name || acc.user?.email || acc.user_id}</p>
                    <p className="text-gray-500 text-xs">{acc.user?.email}</p>
                  </td>
                  <td className="p-3"><span className="px-2 py-0.5 rounded-full text-xs bg-emerald-600/10 text-emerald-400 capitalize">{acc.plan_type?.replace('_', ' ')}</span></td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${acc.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : acc.status === 'trial' ? 'bg-blue-500/10 text-blue-400' : 'bg-red-500/10 text-red-400'}`}>{acc.status}</span></td>
                  <td className="p-3 text-gray-400 text-sm">${acc.amount?.toFixed(2) || '—'}</td>
                  <td className="p-3 text-right flex gap-1 justify-end">
                    {acc.status === 'active' && <Button size="sm" onClick={() => updateAccount(acc.id, { status: 'cancelled' })} className="text-xs bg-red-600/20 text-red-400 hover:bg-red-600/30">Cancel</Button>}
                    {acc.status === 'cancelled' && <Button size="sm" onClick={() => updateAccount(acc.id, { status: 'active' })} className="text-xs bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30">Reactivate</Button>}
                  </td>
                </tr>
              ))}
              {accounts.length === 0 && <tr><td colSpan={5} className="text-center text-gray-500 py-8">No accounts found</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAccounts;
